import { useEffect, useMemo, useRef } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTenant } from '../../contexts/TenantContext';
import { useService } from '../../contexts/ServiceContext';
import { useWebSocketContext } from '../../contexts/WebSocketContext';
import { loadAndInitPlugin } from './plugin-loader';
import { createNgwHandlers } from './handlers';
import { resolveMediaSyncConfig, detectEnvironment } from './config';
import type { MediaSyncHandlers, MediaSyncProps } from './config';

export default function MediaSyncPage() {
  const { session } = useAuth();
  const { getConfigValue } = useTenant();
  const {
    searchListingsPaginated,
    searchListings,
    isWebSocketReady,
    getListing,
    initiateMediaUpload,
    saveListingTour,
    saveListing,
  } = useService();
  const { isConnected } = useWebSocketContext();

  const [searchParams] = useSearchParams();
  const hasInitialised = useRef(false);

  const manifestid = searchParams.get('manifestid');
  const debug =
    searchParams.get('debug') === 'true' || searchParams.get('debug') === '1';

  // Live handlers — recreated when deps change (including WebSocket state).
  // Stored in a ref so the plugin (initialized once) always reaches the latest.
  const handlersRef = useRef<MediaSyncHandlers | null>(null);
  handlersRef.current = useMemo(
    () =>
      createNgwHandlers({
        searchListingsPaginated,
        searchListings,
        isWebSocketReady: () => isWebSocketReady() && isConnected,
        getListing,
        initiateMediaUpload,
        saveListingTour,
        saveListing,
      }),
    [
      searchListingsPaginated,
      searchListings,
      isWebSocketReady,
      isConnected,
      getListing,
      initiateMediaUpload,
      saveListingTour,
      saveListing,
    ],
  );

  // Stable proxy with permanent identity — safe for the plugin to hold onto.
  const stableHandlers = useRef<MediaSyncHandlers>({
    fetchListings: (push) => handlersRef.current!.fetchListings(push),
    fetchListingDetails: (partial, push) =>
      handlersRef.current!.fetchListingDetails(partial, push),
    uploadMedia: (media, listing, context) =>
      handlersRef.current!.uploadMedia(media, listing, context),
    importStart: (entries, listing, context) =>
      handlersRef.current!.importStart?.(entries, listing, context) ??
      Promise.resolve(),
    importComplete: (context) => handlersRef.current!.importComplete(context),
  }).current;

  const mediaSyncProps = useMemo<MediaSyncProps | null>(() => {
    if (!session) return null;

    const env = detectEnvironment();

    const tenantConfig: Record<string, string> = {};
    const keys = [
      'MEDIA_SYNC_API_KEY',
      'MEDIA_SYNC_API_USER',
      'MEDIA_SYNC_PLUGIN_URL',
      'MEDIA_PROVIDER_GOOGLE_DRIVE_API_KEY',
    ];
    for (const key of keys) {
      const val = getConfigValue<string>(key);
      if (val) tenantConfig[key] = val;
    }

    const config = resolveMediaSyncConfig(tenantConfig, env);

    const agent = session.user?.agent;
    const agentName = agent
      ? `${agent.MemberPreferredFirstName ?? agent.MemberFirstName ?? ''} ${agent.MemberPreferredLastName ?? agent.MemberLastName ?? ''}`.trim()
      : '';

    return { ...config, agentName, handlers: stableHandlers };
  }, [session, getConfigValue, stableHandlers]);

  useEffect(() => {
    if (hasInitialised.current) return;
    if (!manifestid) return;
    if (!mediaSyncProps) return;

    hasInitialised.current = true;

    // Load Material Symbols Outlined font for the plugin's icons
    if (!document.querySelector('link[href*="Material+Symbols+Outlined"]')) {
      const fontLink = document.createElement('link');
      fontLink.rel = 'stylesheet';
      fontLink.href =
        'https://fonts.googleapis.com/css2?family=Material+Symbols+Outlined';
      document.head.appendChild(fontLink);
    }

    loadAndInitPlugin(mediaSyncProps, debug);
  }, [manifestid, debug, mediaSyncProps]);

  if (!manifestid) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '80vh',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        <Typography variant="h5" color="error">
          Missing Import Link
        </Typography>
        <Typography variant="body1" color="text.secondary">
          No manifest ID was provided. Please use a valid import link.
        </Typography>
        <Typography variant="caption" color="text.disabled">
          Expected URL: /media-sync?manifestid=your-manifest-id
        </Typography>
      </Box>
    );
  }

  if (!mediaSyncProps) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '80vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
        overflow: 'hidden',
      }}
    >
      <div className="media-box" id="media-sync-container" />
    </Box>
  );
}
